import React, { useState, useEffect } from 'react';
import {
    X, Move, Building2, MapPin, DollarSign, Calendar, User,
    CheckCircle, AlertCircle, Info, ArrowRight, Home, CreditCard,
    ChevronLeft, ChevronRight, RefreshCw, FileText, Shield
} from 'lucide-react';
import api from '../services/api';
import Modal from './Common/Modal';

const TransferModal = ({ isOpen, onClose, lease, onTransferComplete }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [availableUnits, setAvailableUnits] = useState([]);
    const [preview, setPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [transferResult, setTransferResult] = useState(null);
    const [assets, setAssets] = useState([]);
    const [selectedAssetId, setSelectedAssetId] = useState('all');

    // Form data
    const [formData, setFormData] = useState({
        lease_id: lease?.id || '',
        to_asset_unit_id: '',
        transfer_date: new Date().toISOString().split('T')[0],
        move_out_date: new Date().toISOString().split('T')[0],
        move_in_date: new Date().toISOString().split('T')[0],
        deposit_action: 'transfer',
        reason: '',
        end_date: '',
        rent_amount: '',
        deposit_amount: '',
        payment_due_day: '',
        terms_and_conditions: ''
    });

    const totalSteps = 5;

    useEffect(() => {
        if (isOpen && lease?.id) {
            setFormData(prev => ({
                ...prev,
                lease_id: lease.id,
                end_date: '',
                rent_amount: '',
                deposit_amount: '',
                payment_due_day: lease.payment_due_day || 1
            }));
            setCurrentStep(1);
            setTransferResult(null);
            setPreview(null);
            setSelectedAssetId('all');
            fetchAvailableUnits(lease.id);
            fetchAssets(lease.id);
        }
    }, [isOpen, lease]);

    const fetchAssets = async (leaseId) => {
        try {
            const response = await api.get('/assets');
            const assetsData = response.data.data?.data || [];
            setAssets(assetsData);

            // Get current lease info to find current asset
            if (lease?.asset_name) {
                const currentAsset = assetsData.find(a => a.name === lease.asset_name);
                if (currentAsset) {
                    setSelectedAssetId(currentAsset.id);
                }
            }
        } catch (error) {
            console.error('Error fetching assets:', error);
        }
    };

    const fetchAvailableUnits = async (leaseId) => {
        try {
            setLoading(true);
            const params = { lease_id: leaseId };
            if (selectedAssetId !== 'all') {
                params.asset_id = selectedAssetId;
            }
            const response = await api.get('/transfers/available-units', { params });
            setAvailableUnits(response.data.data || []);
        } catch (error) {
            console.error('Error fetching available units:', error);
            setAvailableUnits([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPreview = async () => {
        if (!formData.to_asset_unit_id) {
            alert('Please select a destination unit first');
            return;
        }

        try {
            setLoading(true);
            const response = await api.post('/transfers/preview', {
                lease_id: formData.lease_id,
                to_asset_unit_id: formData.to_asset_unit_id,
                transfer_date: formData.transfer_date,
                move_out_date: formData.move_out_date,
                move_in_date: formData.move_in_date,
                deposit_action: formData.deposit_action,
                new_rent_amount: formData.rent_amount || undefined
            });
            setPreview(response.data.data);

            // Auto-populate rent from preview
            if (!formData.rent_amount && response.data.data?.destination?.rent_amount) {
                setFormData(prev => ({
                    ...prev,
                    rent_amount: response.data.data.destination.rent_amount
                }));
            }
        } catch (error) {
            console.error('Error fetching preview:', error);
            alert('Failed to calculate transfer preview');
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteTransfer = async () => {
        if (!formData.reason) {
            alert('Please provide a reason for the transfer');
            return;
        }
        if (!formData.end_date) {
            alert('Please set the new lease end date');
            return;
        }
        if (!formData.to_asset_unit_id) {
            alert('Please select a destination unit');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                rent_amount: parseFloat(formData.rent_amount) || undefined,
                deposit_amount: parseFloat(formData.deposit_amount) || undefined,
                payment_due_day: parseInt(formData.payment_due_day) || 1
            };

            const response = await api.post('/transfers', payload);
            setTransferResult(response.data.data);
            setCurrentStep(5);

            if (onTransferComplete) {
                onTransferComplete(response.data.data);
            }
        } catch (error) {
            console.error('Error executing transfer:', error);
            alert(error.response?.data?.error || 'Failed to execute transfer');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSelectUnit = (unitId) => {
        setFormData(prev => ({
            ...prev,
            to_asset_unit_id: unitId
        }));
    };

    const formatCurrency = (amount) => {
        return `KES ${(amount || 0).toLocaleString()}`;
    };

    const handleNext = async () => {
        if (currentStep === 1) {
            // Validate unit selected
            if (!formData.to_asset_unit_id) {
                alert('Please select a destination unit');
                return;
            }
            // Fetch preview
            await fetchPreview();
        }
        if (currentStep === 2) {
            if (!formData.reason) {
                alert('Please provide a reason for the transfer');
                return;
            }
        }
        if (currentStep === 3) {
            if (!formData.end_date) {
                alert('Please set the new lease end date');
                return;
            }
        }
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    };

    const handlePrev = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    if (!isOpen) return null;

    const renderStepIndicator = () => (
        <div className="flex items-center justify-center mb-8 space-x-2">
            {[1, 2, 3, 4, 5].map((step) => (
                <React.Fragment key={step}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        currentStep >= step
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-500'
                    }`}>
                        {currentStep > step ? (
                            <CheckCircle className="h-5 w-5" />
                        ) : (
                            step
                        )}
                    </div>
                    {step < 5 && (
                        <div className={`w-8 h-0.5 ${
                            currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'
                        }`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    const renderStepTitle = () => {
        const titles = {
            1: 'Select Destination Unit',
            2: 'Set Transfer Dates',
            3: 'Financial Settlement',
            4: 'Review & Confirm',
            5: 'Transfer Complete'
        };
        return (
            <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                    {currentStep === 5 ? '🎉 Transfer Complete!' : titles[currentStep]}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Step {currentStep} of {totalSteps}
                </p>
            </div>
        );
    };

    // ==================== STEP 1: Select Destination Unit ====================
    const renderStep1 = () => (
        <div className="space-y-4">
            {/* Current Unit Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                    <Home className="h-5 w-5 text-blue-600" />
                    <h4 className="font-medium text-blue-900">Current Unit</h4>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                    <span>Property: <strong>{lease?.asset_name || 'N/A'}</strong></span>
                    <span>Unit: <strong>{lease?.unit_number || 'N/A'}</strong></span>
                    <span>Rent: <strong>{formatCurrency(lease?.rent_amount)}</strong></span>
                    <span>Tenant: <strong>{lease?.first_name} {lease?.last_name}</strong></span>
                </div>
            </div>

            {/* Asset Filter */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Filter by Property
                </label>
                <select
                    value={selectedAssetId}
                    onChange={(e) => {
                        setSelectedAssetId(e.target.value);
                        setFormData(prev => ({ ...prev, to_asset_unit_id: '' }));
                    }}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                    <option value="all">All Properties</option>
                    {assets.map(asset => (
                        <option key={asset.id} value={asset.id}>
                            {asset.name} ({asset.available_units || 0} available)
                        </option>
                    ))}
                </select>
            </div>

            {/* Available Units */}
            {loading ? (
                <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-indigo-600" />
                    <p className="mt-2 text-sm text-gray-500">Loading available units...</p>
                </div>
            ) : availableUnits.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Building2 className="h-12 w-12 mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500 font-medium">No Available Units</p>
                    <p className="text-sm text-gray-400">
                        All units in {selectedAssetId !== 'all' ? 'this property' : 'all properties'} are currently occupied or unavailable.
                    </p>
                </div>
            ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                    {availableUnits.map(unit => (
                        <div
                            key={unit.id}
                            onClick={() => handleSelectUnit(unit.id)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                formData.to_asset_unit_id === unit.id
                                    ? 'border-indigo-500 bg-indigo-50 shadow-md'
                                    : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                            }`}
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <h5 className="font-medium text-gray-900">
                                        {unit.asset_name} - Unit {unit.unit_number}
                                    </h5>
                                    {unit.name && (
                                        <p className="text-sm text-gray-500">{unit.name}</p>
                                    )}
                                    <div className="flex items-center mt-1 space-x-4">
                                        <span className="text-sm text-gray-600">
                                            <DollarSign className="inline h-3 w-3" />
                                            {formatCurrency(unit.effective_rate)}/month
                                        </span>
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                            {unit.category}
                                        </span>
                                    </div>
                                </div>
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    formData.to_asset_unit_id === unit.id
                                        ? 'border-indigo-500 bg-indigo-500'
                                        : 'border-gray-300'
                                }`}>
                                    {formData.to_asset_unit_id === unit.id && (
                                        <div className="w-2 h-2 bg-white rounded-full" />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Different Property Notice */}
            {selectedAssetId === 'all' && (
                <div className="bg-yellow-50 p-3 rounded-lg flex items-start space-x-2">
                    <Info className="h-4 w-4 text-yellow-600 mt-0.5" />
                    <p className="text-sm text-yellow-800">
                        Units from different properties are shown. Transfers across properties may have different deposit and rent structures.
                    </p>
                </div>
            )}
        </div>
    );

    // ==================== STEP 2: Set Transfer Dates ====================
    const renderStep2 = () => (
        <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="h-5 w-5 text-indigo-600" />
                    <h4 className="font-medium text-gray-900">Transfer Dates</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    Set the dates for the move. If same-day, the tenant moves directly from old unit to new unit on the same date.
                </p>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Transfer Date *
                        </label>
                        <input
                            type="date"
                            value={formData.transfer_date}
                            onChange={(e) => {
                                setFormData(prev => ({
                                    ...prev,
                                    transfer_date: e.target.value,
                                    move_out_date: e.target.value,
                                    move_in_date: e.target.value
                                }));
                            }}
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Move-Out Date
                            </label>
                            <input
                                type="date"
                                value={formData.move_out_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, move_out_date: e.target.value }))}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Move-In Date
                            </label>
                            <input
                                type="date"
                                value={formData.move_in_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, move_in_date: e.target.value }))}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                        </div>
                    </div>

                    {/* Date gap warning */}
                    {formData.move_out_date !== formData.move_in_date && (
                        <div className="bg-yellow-50 p-3 rounded-lg flex items-start space-x-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-yellow-800">Different Move-Out and Move-In Dates</p>
                                <p className="text-xs text-yellow-700 mt-1">
                                    {new Date(formData.move_out_date) < new Date(formData.move_in_date)
                                        ? 'There will be a gap in occupancy. Ensure the tenant has accommodation during this period.'
                                        : 'Dates overlap. Tenant will be responsible for both units during overlap period (paying double rent).'
                                    }
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Transfer Reason */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    <AlertCircle className="inline h-4 w-4 mr-1" />
                    Reason for Transfer *
                </label>
                <select
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                    <option value="">Select a reason...</option>
                    <option value="tenant_requested">Tenant Requested</option>
                    <option value="upsizing">Upgrading (Moving to Larger Unit)</option>
                    <option value="downsizing">Downsizing (Moving to Smaller Unit)</option>
                    <option value="property_issue">Property Issue (Maintenance/Repair)</option>
                    <option value="same_property">Moving Between Units (Same Property)</option>
                    <option value="different_property">Moving to Different Property</option>
                    <option value="landlord_requested">Landlord Requested</option>
                    <option value="other">Other</option>
                </select>
                {formData.reason === 'other' && (
                    <input
                        type="text"
                        value={formData.reason_custom || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, reason_custom: e.target.value, reason: e.target.value }))}
                        placeholder="Describe the reason..."
                        className="mt-2 w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                )}
            </div>
        </div>
    );

    // ==================== STEP 3: Financial Settlement ====================
    const renderStep3 = () => (
        <div className="space-y-4">
            {loading ? (
                <div className="text-center py-8">
                    <RefreshCw className="h-8 w-8 animate-spin mx-auto text-indigo-600" />
                    <p className="mt-2 text-sm text-gray-500">Calculating financials...</p>
                </div>
            ) : preview ? (
                <>
                    {/* Prorated Rent Summary */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center space-x-2 mb-3">
                            <DollarSign className="h-5 w-5 text-blue-600" />
                            <h4 className="font-medium text-blue-900">Prorated Rent Summary</h4>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-blue-700">Old Unit Prorated (Final Month):</span>
                                <span className="font-medium text-blue-900">{formatCurrency(preview.prorated.old_rent)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-blue-700">New Unit Prorated (First Month):</span>
                                <span className="font-medium text-blue-900">{formatCurrency(preview.prorated.new_rent)}</span>
                            </div>
                            <div className="flex justify-between border-t border-blue-200 pt-2">
                                <span className="font-medium text-blue-800">Total Prorated:</span>
                                <span className="font-bold text-blue-900">{formatCurrency(preview.prorated.total_prorated)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Deposit Handling */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <CreditCard className="inline h-4 w-4 mr-1" />
                            Security Deposit Action
                        </label>
                        <div className="space-y-2">
                            {['transfer', 'partial_refund', 'full_refund'].map(action => (
                                <label
                                    key={action}
                                    className={`flex items-center p-3 rounded-lg border-2 cursor-pointer ${
                                        formData.deposit_action === action
                                            ? 'border-indigo-500 bg-indigo-50'
                                            : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="deposit_action"
                                        value={action}
                                        checked={formData.deposit_action === action}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, deposit_action: e.target.value }));
                                        }}
                                        className="mr-3"
                                    />
                                    <div>
                                        <p className="font-medium text-sm text-gray-900">
                                            {action === 'transfer' ? 'Full Transfer' :
                                             action === 'partial_refund' ? 'Partial Refund' :
                                             'Full Refund'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {action === 'transfer'
                                                ? `Transfer ${formatCurrency(preview.deposit.transfer.deposit_transferred)} to new unit`
                                                : action === 'partial_refund'
                                                    ? `Refund ${formatCurrency(preview.deposit.partial_refund.deposit_refunded)}, transfer ${formatCurrency(preview.deposit.partial_refund.deposit_transferred)}`
                                                    : `Refund ${formatCurrency(preview.deposit.full_refund.deposit_refunded)}, tenant pays new deposit of ${formatCurrency(preview.deposit.full_refund.deposit_top_up)}`
                                            }
                                            {preview.deposit[action]?.deposit_top_up > 0 && (
                                                <span className="block text-orange-600 mt-1">
                                                    ⚠️ Additional deposit of {formatCurrency(preview.deposit[action].deposit_top_up)} required
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Deposit Movement Details */}
                    {preview.deposit[formData.deposit_action] && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-medium text-sm text-gray-700 mb-2">Deposit Movement</h5>
                            <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span>Current Deposit:</span>
                                    <span>{formatCurrency(preview.current.deposit)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Transferred:</span>
                                    <span className="text-green-600">{formatCurrency(preview.deposit[formData.deposit_action].deposit_transferred)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Refunded:</span>
                                    <span>{formatCurrency(preview.deposit[formData.deposit_action].deposit_refunded)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Top-Up Required:</span>
                                    <span className="text-orange-600">{formatCurrency(preview.deposit[formData.deposit_action].deposit_top_up)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <DollarSign className="h-12 w-12 mx-auto text-gray-400" />
                    <p className="mt-2 text-gray-500">Click "Next" to calculate financial preview</p>
                </div>
            )}

            {/* New Lease Terms */}
            <div className="border-t pt-4">
                <h5 className="font-medium text-sm text-gray-700 mb-3">New Lease Terms</h5>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <DollarSign className="inline h-3 w-3" />
                                Rent Amount (KES)
                            </label>
                            <input
                                type="number"
                                value={formData.rent_amount}
                                onChange={(e) => setFormData(prev => ({ ...prev, rent_amount: e.target.value }))}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder={preview?.destination?.rent_amount || 'Enter amount'}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Security Deposit (KES)
                            </label>
                            <input
                                type="number"
                                value={formData.deposit_amount}
                                onChange={(e) => setFormData(prev => ({ ...prev, deposit_amount: e.target.value }))}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder={preview?.current?.deposit || 'Optional'}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Payment Due Day
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="28"
                                value={formData.payment_due_day}
                                onChange={(e) => setFormData(prev => ({ ...prev, payment_due_day: e.target.value }))}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <Calendar className="inline h-3 w-3" />
                                New Lease End Date *
                            </label>
                            <input
                                type="date"
                                value={formData.end_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // ==================== STEP 4: Review & Confirm ====================
    const renderStep4 = () => (
        <div className="space-y-4">
            <div className="bg-yellow-50 p-4 rounded-lg flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                    <p className="text-sm font-medium text-yellow-800">Please review all details before confirming</p>
                    <p className="text-xs text-yellow-700 mt-1">
                        This action will terminate the current lease, create a new lease for the destination unit, 
                        and handle all financial adjustments.
                    </p>
                </div>
            </div>

            {/* Transfer Overview */}
            <div className="bg-white border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                    <h5 className="font-medium text-gray-700">Transfer Overview</h5>
                </div>
                <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <Home className="h-4 w-4 text-red-500" />
                            <span className="text-sm">From:</span>
                        </div>
                        <span className="text-sm font-medium">
                            {lease?.asset_name} - Unit {lease?.unit_number}
                        </span>
                    </div>
                    <div className="flex justify-center">
                        <ArrowRight className="h-5 w-5 text-indigo-500" />
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <Home className="h-4 w-4 text-green-500" />
                            <span className="text-sm">To:</span>
                        </div>
                        <span className="text-sm font-medium">
                            {availableUnits.find(u => u.id === formData.to_asset_unit_id)?.asset_name} - 
                            Unit {availableUnits.find(u => u.id === formData.to_asset_unit_id)?.unit_number}
                        </span>
                    </div>
                </div>
            </div>

            {/* Date Details */}
            <div className="bg-white border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                    <h5 className="font-medium text-gray-700">Date Details</h5>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <span className="text-gray-500">Transfer Date:</span>
                        <p className="font-medium">{new Date(formData.transfer_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Move-Out:</span>
                        <p className="font-medium">{new Date(formData.move_out_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Move-In:</span>
                        <p className="font-medium">{new Date(formData.move_in_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                        <span className="text-gray-500">Reason:</span>
                        <p className="font-medium capitalize">{formData.reason.replace('_', ' ')}</p>
                    </div>
                </div>
            </div>

            {/* Financial Summary */}
            {preview && (
                <div className="bg-white border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b">
                        <h5 className="font-medium text-gray-700">Financial Summary</h5>
                    </div>
                    <div className="p-4 space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span>New Monthly Rent:</span>
                            <span className="font-medium">{formatCurrency(formData.rent_amount || preview.destination.rent_amount)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Prorated Old Rent:</span>
                            <span>{formatCurrency(preview.prorated.old_rent)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Prorated New Rent:</span>
                            <span>{formatCurrency(preview.prorated.new_rent)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                            <span>Deposit Action:</span>
                            <span className="font-medium capitalize">{formData.deposit_action.replace('_', ' ')}</span>
                        </div>
                        {preview.deposit[formData.deposit_action]?.deposit_top_up > 0 && (
                            <div className="flex justify-between text-orange-600">
                                <span>Deposit Top-Up:</span>
                                <span className="font-medium">{formatCurrency(preview.deposit[formData.deposit_action].deposit_top_up)}</span>
                            </div>
                        )}
                        <div className="flex justify-between border-t pt-2 text-base">
                            <span className="font-semibold">New Lease End:</span>
                            <span className="font-semibold">{new Date(formData.end_date).toLocaleDateString()}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Terms */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Terms (Optional)
                </label>
                <textarea
                    value={formData.terms_and_conditions}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                    rows={3}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Any special conditions for this transfer..."
                />
            </div>

            {/* Confirm Checkbox */}
            <div className="bg-indigo-50 p-3 rounded-lg">
                <label className="flex items-start space-x-2">
                    <input
                        type="checkbox"
                        id="confirm-transfer"
                        className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        required
                    />
                    <span className="text-sm text-indigo-900">
                        I confirm that I have reviewed all the details above and authorize this tenant transfer. 
                        The current lease will be terminated and a new lease will be created. 
                        All financial adjustments will be applied as shown.
                    </span>
                </label>
            </div>
        </div>
    );

    // ==================== STEP 5: Transfer Complete ====================
    const renderStep5 = () => (
        <div className="text-center space-y-6">
            <div className="flex items-center justify-center">
                <div className="bg-green-100 rounded-full p-4">
                    <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
            </div>

            <div>
                <h3 className="text-2xl font-bold text-gray-900">Tenant Relocated Successfully!</h3>
                <p className="text-gray-500 mt-2">The tenant has been moved to their new unit.</p>
            </div>

            {transferResult && (
                <div className="bg-white border rounded-lg overflow-hidden text-left">
                    <div className="bg-green-50 px-4 py-2 border-b">
                        <h5 className="font-medium text-green-800">Transfer Summary</h5>
                    </div>
                    <div className="p-4 space-y-3 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">New Lease Number:</span>
                            <span className="font-medium text-green-700">{transferResult.new_lease_number}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Old Lease:</span>
                            <span className="font-medium">{transferResult.transfer?.old_lease_number}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Prorated Old Rent:</span>
                            <span>{formatCurrency(transferResult.prorated_old_rent)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Prorated New Rent:</span>
                            <span>{formatCurrency(transferResult.prorated_new_rent)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-500">Deposit Transferred:</span>
                            <span className="font-medium">{formatCurrency(transferResult.deposit_transferred)}</span>
                        </div>
                        {transferResult.deposit_refunded > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Deposit Refunded:</span>
                                <span className="font-medium text-green-600">{formatCurrency(transferResult.deposit_refunded)}</span>
                            </div>
                        )}
                        {transferResult.deposit_top_up > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Deposit Top-Up:</span>
                                <span className="font-medium text-orange-600">{formatCurrency(transferResult.deposit_top_up)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="bg-blue-50 p-4 rounded-lg text-left">
                <div className="flex items-center space-x-2 mb-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    <h5 className="font-medium text-blue-800">What Happens Next?</h5>
                </div>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>The old unit is now marked as <strong>available</strong></li>
                    <li>The new unit is marked as <strong>rented</strong></li>
                    <li>A new lease has been created for the destination unit</li>
                    <li>Prorated rent amounts have been recorded</li>
                    <li>Deposit adjustments are reflected in the system</li>
                    <li>An audit trail has been created for this transfer</li>
                </ul>
            </div>

            <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
                Close
            </button>
        </div>
    );

    // ==================== RENDER STEPS ====================
    const renderStepContent = () => {
        switch (currentStep) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            case 4: return renderStep4();
            case 5: return renderStep5();
            default: return null;
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
            <div className="px-2">
                {currentStep !== 5 && (
                    <>
                        {renderStepIndicator()}
                        {renderStepTitle()}
                    </>
                )}

                {renderStepContent()}

                {/* Navigation Buttons (hidden on step 5) */}
                {currentStep !== 5 && (
                    <div className="flex justify-between mt-6 pt-4 border-t">
                        <button
                            onClick={currentStep === 1 ? onClose : handlePrev}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            {currentStep === 1 ? 'Cancel' : 'Back'}
                        </button>

                        {currentStep < 4 ? (
                            <button
                                onClick={handleNext}
                                disabled={loading}
                                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </>
                                )}
                            </button>
                        ) : currentStep === 4 ? (
                            <button
                                onClick={handleExecuteTransfer}
                                disabled={submitting}
                                className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                {submitting ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="h-4 w-4 mr-1" />
                                        Execute Transfer
                                    </>
                                )}
                            </button>
                        ) : null}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default TransferModal;
